import { Controller } from "@hotwired/stimulus"

// Combobox: type to filter a fixed options list, arrow/enter/click to select.
export default class extends Controller {
  static targets = ["input", "list", "clear"]
  static values = {
    options: { type: Array, default: [] },
    open: { type: Boolean, default: false }
  }

  connect() {
    this.activeIndex = -1
    this._onDocPointer = this.#onDocPointer.bind(this)
    document.addEventListener("pointerdown", this._onDocPointer)
    this.#syncClear()
    this.#render()
  }

  disconnect() {
    document.removeEventListener("pointerdown", this._onDocPointer)
  }

  open() {
    this.openValue = true
    this.#render()
  }

  close() {
    this.openValue = false
    this.activeIndex = -1
    this.#render()
  }

  filter() {
    this.openValue = true
    this.activeIndex = 0
    this.#syncClear()
    this.#render()
  }

  select(event) {
    const value = event.currentTarget.dataset.value
    this.inputTarget.value = value
    this.#syncClear()
    this.close()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clear(event) {
    event.preventDefault()
    this.inputTarget.value = ""
    this.#syncClear()
    this.close()
    this.inputTarget.focus()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  keydown(event) {
    const matches = this.#matches()
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.openValue = true
        this.activeIndex = Math.min(this.activeIndex + 1, matches.length - 1)
        this.#render()
        break
      case "ArrowUp":
        event.preventDefault()
        this.openValue = true
        this.activeIndex = Math.max(this.activeIndex - 1, 0)
        this.#render()
        break
      case "Enter":
        if (this.openValue && this.activeIndex >= 0 && matches[this.activeIndex]) {
          event.preventDefault()
          this.inputTarget.value = matches[this.activeIndex]
          this.#syncClear()
          this.close()
          this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
        }
        break
      case "Escape":
        if (this.openValue) {
          event.preventDefault()
          this.close()
        }
        break
      case "Tab":
        this.close()
        break
    }
  }

  #onDocPointer(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  #syncClear() {
    if (!this.hasClearTarget) return
    const show = this.inputTarget.value.trim().length > 0
    this.clearTarget.hidden = !show
    this.clearTarget.classList.toggle("is-visible", show)
  }

  #matches() {
    const q = this.inputTarget.value.trim().toLowerCase()
    const opts = this.optionsValue.map(String)
    if (!q) return opts
    return opts.filter((opt) => opt.toLowerCase().includes(q))
  }

  #render() {
    if (!this.hasListTarget) return

    const matches = this.#matches()
    if (this.activeIndex >= matches.length) this.activeIndex = matches.length - 1

    this.listTarget.hidden = !this.openValue
    this.inputTarget.setAttribute("aria-expanded", this.openValue ? "true" : "false")

    if (!this.openValue) {
      this.listTarget.innerHTML = ""
      return
    }

    if (matches.length === 0) {
      this.listTarget.innerHTML =
        '<li class="cp-typeahead-empty" role="presentation">No matches</li>'
      return
    }

    this.listTarget.innerHTML = matches
      .map((opt, i) => {
        const active = i === this.activeIndex ? " is-active" : ""
        const selected = opt === this.inputTarget.value ? ' aria-selected="true"' : ""
        return (
          `<li class="cp-typeahead-option${active}" role="option"${selected}` +
          ` data-action="pointerdown->typeahead#select"` +
          ` data-value="${this.#escapeAttr(opt)}">${this.#escapeHtml(opt)}</li>`
        )
      })
      .join("")

    const activeEl = this.listTarget.querySelector(".is-active")
    if (activeEl) activeEl.scrollIntoView({ block: "nearest" })
  }

  #escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
  }

  #escapeAttr(value) {
    return this.#escapeHtml(value).replaceAll("'", "&#39;")
  }
}
